/**
 * Helpers for capturing and scrubbing the `?early_access=...` URL parameter.
 *
 * The redemption flow:
 *   1. User opens an invite link, e.g. https://vellum/?early_access=abc123
 *   2. EarlyAccessProvider reads the token out of the URL
 *   3. Provider POSTs it to /api/early-access/redeem
 *   4. Provider strips the param from the URL via history.replaceState so
 *      it disappears from the address bar and the browser history, and so
 *      refreshes don't trigger a second redemption attempt.
 *
 * Keeping these utilities separate from the context makes them easy to unit
 * test without pulling in React.
 */

export const EARLY_ACCESS_QUERY_PARAM = 'early_access';

/** Read the token from the current URL, or null if absent / empty. */
export function readEarlyAccessTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get(EARLY_ACCESS_QUERY_PARAM);
  const trimmed = raw?.trim();
  return trimmed ? trimmed : null;
}

/**
 * Remove the token from the URL without reloading. Uses replaceState so it
 * doesn't add a history entry — the user can still use the back button to
 * return to wherever they came from.
 */
export function stripEarlyAccessTokenFromUrl(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has(EARLY_ACCESS_QUERY_PARAM)) return;
  url.searchParams.delete(EARLY_ACCESS_QUERY_PARAM);
  const newUrl = `${url.pathname}${url.search ? url.search : ''}${url.hash}`;
  window.history.replaceState({}, '', newUrl);
}

/**
 * Build the shareable invite URL for a given token.
 * Used by the admin dashboard's "copy link" button. Defaults to the current
 * origin, which is what admins almost always want; the optional `origin`
 * override is only for preview / testing.
 */
export function buildInviteUrl(token: string, origin?: string): string {
  const base = origin ?? (typeof window !== 'undefined' ? window.location.origin : '');
  return `${base}/?${EARLY_ACCESS_QUERY_PARAM}=${encodeURIComponent(token)}`;
}
